import React, {Component, ReactNode} from 'react'
import AssocManagementRepository from '../assocDirector/AssocManagementRepository'
import AdminGeneralMeetingRepo, {
    GeneralMeetingsSearchCondition,
    GetGeneralMeetingsResponse
} from './AdminGeneralMeetingRepo'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {Nullable} from '../../app/TypeHelpers'
import {AssocOfCondominium} from '../assocDirector/model/AssocOfCondominium'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import {Box, Link, TableCell, TableRow} from '@mui/material'
import {SelectChangeEvent} from '@mui/material/Select'
import {SortDirection} from '@mui/material/TableCell'
import {LeftRowFlexContainer} from '../../styledComponents/FlexComponents'
import {
    AssocItemName,
    convertAssocListToAssocSelectList,
    TransparentSelect
} from '../assocDirector/shared/AssocComponents'
import {OptionProps} from '../shared/forms/PPSelect'
import {judgeAuthorityCheck} from '../../__NoAutomatedTests/common/util/Natutil'
import {AdminDefaultButton} from '../../styledComponents/adminPage/Buttons'
import {SortOrder} from '../shared/SortOrder'
import {COMMON_CONSTANTS} from '../../CommonConstants'
import SortableTableHeader, {
    SortableTableHeaderCell
} from '../../styledComponents/adminPage/SortablePaginationTableHeader'
import {PageMainArea} from '../shared/hoc/withAdminPageTemplate'
import SortablePaginationTable from '../../styledComponents/adminPage/SortablePaginationTable'
import SortablePaginationTableBody from '../../styledComponents/adminPage/SortablePaginationTableBody'
import {GENERAL_MEETING_STATUS} from '../../CodeMaster'
import {NoWrappedTableCell} from '../../styledComponents/adminPage/Table'
import styled from 'styled-components'
import {SessionStorageHelper} from '../../SessionStorageHelper'
import {KEY_GENERAL_MEETING_LIST_SEARCH_CONDITION} from '../../app/AdminSessionUtil'
import {AdminGeneralMeetingInfo} from './model/GeneralMeeting'
import {formatForDateInputFromDate} from '../../formatters/formatDate'
import {Location} from 'react-router-dom'
import {HeadingAreaMessageParams} from '../shared/pageBaseComponents/HeadingAreaMessageComponent'

interface LocationState {
    initialMessage: HeadingAreaMessageParams
}

interface Props {
    location: Location
    condominiumCd: string
    assocManagementRepo: AssocManagementRepository
    adminGeneralMeetingRepo: AdminGeneralMeetingRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExAdmin
}

interface State {
    assocList: AssocOfCondominium[]
    assocSelectList: OptionProps[]
    selectedAssocId: number
    editMode: boolean
    sortOrders: SortOrder[]
    rowsPerPage: number
    page: number
    totalContentsCount: number
    generalMeetingInfoList: AdminGeneralMeetingInfo[]
}

type SearchConditionForSessionStorage = Omit<GeneralMeetingsSearchCondition, 'sortOrders' | 'page'>

class AdminGeneralMeetingListPage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        const sessionStorageSearchCondition: Nullable<SearchConditionForSessionStorage>
            = SessionStorageHelper.getObject(KEY_GENERAL_MEETING_LIST_SEARCH_CONDITION)

        this.state = {
            assocList: [],
            assocSelectList: [],
            selectedAssocId: 0,
            editMode: false,
            sortOrders: [
                new SortOrder('generalMeetingDate', 'desc'),
                new SortOrder('generalMeetingStatus', 'asc'),
                new SortOrder('releaseDate', 'desc'),
                new SortOrder('updateDatetime', 'desc'),
            ],
            rowsPerPage:
                sessionStorageSearchCondition?.rowsPerPage ?? COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[2],
            page: COMMON_CONSTANTS.FIRST_PAGE,
            totalContentsCount: 0,
            generalMeetingInfoList: []
        }

        this.updateGeneralMeetingList = this.updateGeneralMeetingList.bind(this)
        this.handleChangeAssocListSelect = this.handleChangeAssocListSelect.bind(this)
        this.handleNewGeneralMeetingButton = this.handleNewGeneralMeetingButton.bind(this)
        this.handlePageChange = this.handlePageChange.bind(this)
        this.handleRowsPerPageChange = this.handleRowsPerPageChange.bind(this)
        this.handleSortGeneralMeetingList = this.handleSortGeneralMeetingList.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {
            condominiumCd,
            assocManagementRepo,
            eventEmitter,
            asyncCallFunction,
            navigate,
        } = this.props

        await eventEmitter.showLoading()
        try {
            const editMode: boolean = judgeAuthorityCheck('EDIT-GENERAL_MEETING')

            const assocList: Nullable<AssocOfCondominium[]> =
                await asyncCallFunction(
                    assocManagementRepo.getAssocList.bind(assocManagementRepo, condominiumCd),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(eventEmitter)
                    },
                )

            if (assocList === null) return

            if (assocList.length === 0) {
                navigate('/admin/error')
                return
            }

            const assocSelectList = convertAssocListToAssocSelectList(assocList)

            if (assocSelectList.length <= 1) {
                this.setState({
                    assocSelectList,
                    selectedAssocId: assocList[0].assocId,
                })
            } else {
                const sessionStorageSearchCondition: Nullable<SearchConditionForSessionStorage>
                    = SessionStorageHelper.getObject(KEY_GENERAL_MEETING_LIST_SEARCH_CONDITION)

                const assocId = sessionStorageSearchCondition?.assocId;
                let selectedAssocId = 0
                if (assocId !== null) {
                    selectedAssocId = Number(
                        assocSelectList.find(a => a.value === String(assocId))?.value ?? 0
                    );
                }

                this.setState({
                    assocSelectList: [{label: 'すべて', value: '0'}, ...assocSelectList],
                    selectedAssocId,
                })
            }

            this.setState({
                assocList,
                editMode,
            }, async () => {
                await this.updateGeneralMeetingList()
            })
        } finally {
            eventEmitter.closeLoading()
        }
        this.showInitialMessage()
    }

    async updateGeneralMeetingList(): Promise<void> {
        const {
            condominiumCd,
            adminGeneralMeetingRepo,
            eventEmitter,
            asyncCallFunction,
        } = this.props

        const {
            selectedAssocId,
            sortOrders,
            rowsPerPage,
            page
        } = this.state

        const sortOrderStrings: string[] = []
        if (sortOrders) {
            sortOrders.forEach((sortInfo) => {
                sortOrderStrings.push(`${sortInfo.sortKey}:${sortInfo.sortDirection}`)
            })
        }
        const searchCondition: GeneralMeetingsSearchCondition = {
            assocId: selectedAssocId === 0 ? null : selectedAssocId,
            sortOrders: sortOrderStrings,
            rowsPerPage: rowsPerPage,
            page: page,
        }
        const {
            sortOrders: _sortOrders,
            page: _page,
            ...searchConditionForSessionStorage
        } = searchCondition
        SessionStorageHelper.setObject(KEY_GENERAL_MEETING_LIST_SEARCH_CONDITION, searchConditionForSessionStorage)

        let generalMeetingsResponse: Nullable<GetGeneralMeetingsResponse>
        await eventEmitter.showLoading()
        try {
            generalMeetingsResponse = await asyncCallFunction(
                adminGeneralMeetingRepo.getGeneralMeetings.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    searchCondition,
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
            )
        } finally {
            eventEmitter.closeLoading()
        }

        if (generalMeetingsResponse === null) return

        this.setState({
            totalContentsCount: generalMeetingsResponse.details.totalContentsCount,
            generalMeetingInfoList: generalMeetingsResponse.details.contents,
        })
    }

    showInitialMessage(): void {
        const {
            eventEmitter,
            navigate,
            location
        } = this.props

        if (!location || !location.state) return

        const locationState: LocationState = location.state
        if (!locationState.initialMessage) return

        navigate(location.pathname, {replace: true, state: {}})
        eventEmitter.showHeadingAreaMessage(locationState.initialMessage)
    }

    async handleChangeAssocListSelect(event: SelectChangeEvent<unknown>): Promise<void> {
        this.setState({
            selectedAssocId: Number(event.target.value)
        }, async () => {
            await this.updateGeneralMeetingList()
        })
    }

    handleNewGeneralMeetingButton(): void {
        this.props.navigate(`/admin/condominiums/${this.props.condominiumCd}/general-meetings/register`)
    }

    getSortDirection(sortKey: string): SortDirection {
        const sortOrder: SortOrder | undefined =
            this.state.sortOrders.find(order => order.sortKey === sortKey);
        if (sortOrder === undefined) return false
        return sortOrder.sortDirection
    }

    handlePageChange(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): void {
        this.setState({
            page: newPage,
        }, async () => {
            await this.updateGeneralMeetingList()
        })
    }

    handleRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({
            rowsPerPage: Number(event.target.value),
            page: COMMON_CONSTANTS.FIRST_PAGE,
        }, async () => {
            await this.updateGeneralMeetingList()
        })
    }

    async handleSortGeneralMeetingList(sortKey: string): Promise<void> {
        let sortDirection: SortDirection = this.getSortDirection(sortKey)
        if (sortDirection === false || sortDirection === 'asc') {
            sortDirection = 'desc'
        } else {
            sortDirection = 'asc'
        }

        let sortOrders: SortOrder[] = []
        if (sortKey === 'generalMeetingDate') {
            sortOrders = [
                new SortOrder(sortKey, sortDirection),
                new SortOrder('updateDatetime', sortDirection),
            ]
        } else if (sortKey === 'generalMeetingStatus') {
            sortOrders = [
                new SortOrder(sortKey, sortDirection),
                new SortOrder('releaseDate', sortDirection),
                new SortOrder('updateDatetime', sortDirection),
            ]
        } else {
            return
        }
        this.setState({
            sortOrders
        }, async () => {
            await this.updateGeneralMeetingList()
        })
    }

    renderGeneralMeetingList(): ReactNode {
        const {totalContentsCount, generalMeetingInfoList} = this.state

        if (totalContentsCount === 0) return <>総会の開催案内が登録されていません。</>

        const headers: SortableTableHeaderCell[] = [
            {
                id: 'generalMeetingDate',
                label: '開催日',
                align: 'left',
                sortable: true,
                direction: this.getSortDirection('generalMeetingDate'),
            },
            {
                id: 'generalMeetingName',
                label: '総会名',
                align: 'left',
            },
            {
                id: 'assocName',
                label: '管理組合',
                align: 'left',
            },
            {
                id: 'generalMeetingStatus',
                label: '公開設定',
                align: 'center',
                sortable: true,
                direction: this.getSortDirection('generalMeetingStatus'),
            },
        ]

        return <PageMainArea>
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.totalContentsCount,
                    rowsPerPage: this.state.rowsPerPage,
                    page: this.state.page,
                    onPageChange: this.handlePageChange,
                    onRowsPerPageChange: this.handleRowsPerPageChange,
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                    onSortOrderChange={this.handleSortGeneralMeetingList}
                />
                <SortablePaginationTableBody>
                    {generalMeetingInfoList.map((generalMeeting, index) =>
                        <TableRow key={index}>
                            <NoWrappedTableCell align={'left'} style={{width: '105px'}}>
                                {generalMeeting.generalMeetingDate &&
                                    formatForDateInputFromDate(generalMeeting.generalMeetingDate)}
                            </NoWrappedTableCell>
                            <OverflowWrapTableCell align={'left'} style={{minWidth: '288px'}}>
                                <StyledLink
                                    href={`/admin/condominiums/${this.props.condominiumCd}/general-meetings/${generalMeeting.generalMeetingId}`}>
                                    {generalMeeting.generalMeetingName ?? '登録されていません'}
                                </StyledLink>
                            </OverflowWrapTableCell>
                            <OverflowWrapTableCell align={'left'} style={{minWidth: '440px'}}>
                                {generalMeeting.assocIdNameList.map((assocIdName) =>
                                    <div key={assocIdName.assocId}>{assocIdName.assocName}</div>
                                )}
                            </OverflowWrapTableCell>
                            <NoWrappedTableCell align={'center'} style={{width: '115px'}}>
                                <div>
                                    {generalMeeting.generalMeetingStatus.label}
                                </div>
                                <div>
                                    {(generalMeeting.generalMeetingStatus
                                            === GENERAL_MEETING_STATUS.BEFORE_RELEASE
                                            && generalMeeting.releaseDate)
                                        && formatForDateInputFromDate(generalMeeting.releaseDate)}
                                </div>
                            </NoWrappedTableCell>
                        </TableRow>
                    )}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        </PageMainArea>
    }

    render(): ReactNode {
        const {
            assocList,
            assocSelectList,
            selectedAssocId,
            editMode
        } = this.state

        return <>
            <Box height={'9px'}/>
            {assocList.length > 1 && <>
                <label>
                    <LeftRowFlexContainer>
                        <AssocItemName>管理組合</AssocItemName>
                        <TransparentSelect
                            data={assocSelectList}
                            value={selectedAssocId}
                            onChange={this.handleChangeAssocListSelect}
                        />
                    </LeftRowFlexContainer>
                </label>
                <Box height={'16px'}/>
            </>}
            {editMode && <>
                <AdminDefaultButton onClick={this.handleNewGeneralMeetingButton}>
                    新規
                </AdminDefaultButton>
                <Box height={'16px'}/>
            </>}
            {this.renderGeneralMeetingList()}
        </>
    }
}

export default createAdminPage(AdminGeneralMeetingListPage)

const OverflowWrapTableCell = styled(TableCell)`
  overflow-wrap: anywhere;
`

const StyledLink = styled(Link)`
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`
